import {
  CreateDateColumn,
  UpdateDateColumn,
  Entity,
  Index,
  Column,
  RelationId,
  ManyToOne,
  PrimaryGeneratedColumn
} from 'typeorm'
import { ObjectType, Field, Int, ID, registerEnumType } from 'type-graphql'

import { Domain, ScalarObject } from '@things-factory/shell'
import { User } from '@things-factory/auth-base'

import { DataItem } from '../data-set/data-item-type.js'
import { DataSet, DataSetSummaryPeriodType } from '../data-set/data-set.js'

@Entity()
@Index(
  'ix_data_summary_0',
  (dataSummary: DataSummary) => [
    dataSummary.domain,
    dataSummary.dataSet,
    dataSummary.key01,
    dataSummary.key02,
    dataSummary.key03,
    dataSummary.key04,
    dataSummary.key05,
    dataSummary.date,
    dataSummary.period
  ],
  { unique: true }
)
@Index(
  'ix_data_summary_1',
  (dataSummary: DataSummary) => [dataSummary.domain, dataSummary.dataSet, dataSummary.date, dataSummary.period],
  { unique: false }
)
@Index('ix_data_summary_2', (dataSummary: DataSummary) => [dataSummary.domain, dataSummary.name, dataSummary.date], {
  unique: false
})
@ObjectType({
  description:
    'Represents an aggregated summary of collected data for a dataset, including statistics, counts, and partition keys for reporting and analysis.'
})
export class DataSummary {
  @PrimaryGeneratedColumn('uuid')
  @Field(type => ID, { description: 'Unique identifier for the data summary.' })
  readonly id: string

  @ManyToOne(type => Domain)
  @Field(type => Domain)
  domain?: Domain

  @RelationId((dataSummary: DataSummary) => dataSummary.domain)
  domainId?: string

  @Column()
  @Field({ nullable: true })
  name?: string

  @Column({ nullable: true })
  @Field({ nullable: true })
  description?: string

  @Column({ nullable: true })
  @Field({ nullable: true })
  type?: DataSetSummaryPeriodType

  @Column({ nullable: true, default: '', length: 20 })
  @Field({ nullable: true })
  date?: string = ''

  @Column({ nullable: true, default: '', length: 20 })
  @Field({ nullable: true })
  period?: string = ''

  @ManyToOne(type => DataSet)
  @Field(type => DataSet, { nullable: false })
  dataSet?: DataSet

  @RelationId((dataSample: DataSummary) => dataSample.dataSet)
  dataSetId?: string

  @Column({ nullable: true })
  @Field({ nullable: true })
  dataSetVersion?: number

  @Field(type => [DataItem], { nullable: true })
  dataItems?: DataItem[]

  @Column('simple-json', { nullable: true })
  @Field(type => ScalarObject, { nullable: true, description: 'Aggregated statistical data for this summary period.' })
  summary?: any

  @Column({
    nullable: true
  })
  @Field({ nullable: true, description: 'Total number of data samples in this summary period.' })
  count?: number

  @Column({
    nullable: true
  })
  @Field({ nullable: true, description: 'Number of out-of-control (OOC) samples in this summary period.' })
  countOoc?: number

  @Column({
    nullable: true
  })
  @Field({ nullable: true, description: 'Number of out-of-specification (OOS) samples in this summary period.' })
  countOos?: number

  @Column({ nullable: true, default: '', length: 100 })
  @Field({ nullable: true })
  key01?: string = ''

  @Column({ nullable: true, default: '', length: 100 })
  @Field({ nullable: true })
  key02?: string = ''

  @Column({ nullable: true, default: '', length: 100 })
  @Field({ nullable: true })
  key03?: string = ''

  @Column({ nullable: true, default: '', length: 100 })
  @Field({ nullable: true })
  key04?: string = ''

  @Column({ nullable: true, default: '', length: 100 })
  @Field({ nullable: true })
  key05?: string = ''

  @Column({ type: 'real', nullable: true })
  @Field({ nullable: true })
  data01?: number

  @Column({ type: 'real', nullable: true })
  @Field({ nullable: true })
  data02?: number

  @Column({ type: 'real', nullable: true })
  @Field({ nullable: true })
  data03?: number

  @Column({ type: 'real', nullable: true })
  @Field({ nullable: true })
  data04?: number

  @Column({ type: 'real', nullable: true })
  @Field({ nullable: true })
  data05?: number

  @CreateDateColumn()
  @Field({ nullable: true })
  createdAt?: Date

  @UpdateDateColumn()
  @Field({ nullable: true })
  updatedAt?: Date

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true })
  creator?: User

  @RelationId((dataSummary: DataSummary) => dataSummary.creator)
  creatorId?: string

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true })
  updater?: User

  @RelationId((dataSummary: DataSummary) => dataSummary.updater)
  updaterId?: string
}
